import React, { useEffect } from 'react';
import { Form, Input, Select, message } from 'antd';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import GenderSelect from 'components/form/GenderSelect';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields, SettingEnum } from 'types/enumerationData';
import { IBasicInfoStep } from 'types/newUniprofile';
import { editBasicInfo } from 'api/newUniprofile';
import { useSwitchedProvider } from '../../SwitchedProviderContext';
import commonS from '../../s.module.less';

interface Props {
    initValue?: Partial<IBasicInfoStep> | null;
}

const ProfileCard: React.FC<Props> = ({ initValue }) => {
    const { scheduleToken, onRefresh } = useSwitchedProvider();
    const [credentialTypes] = useGetSettingData(EnumFields.CREDENTIAL_TYPE);
    const [form] = Form.useForm();

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue({
                ...initValue,
                credential:
                    typeof initValue.credential === 'string'
                        ? (initValue.credential as string).split(',')
                        : initValue.credential,
            });
        }
    }, [initValue, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const res = await editBasicInfo(
                {
                    ...values,
                    credential: values.credential.join(','),
                },
                scheduleToken,
            );
            if (res.error) {
                message.error(res.error);
            } else {
                onRefresh?.();
            }
        } catch (error) {
            console.error('Form validation failed:', error);
            throw error;
        }
    };

    const renderEditing = () => (
        <Form form={form} layout="vertical">
            <div className={commonS.row}>
                <Form.Item
                    label="First name"
                    name="firstName"
                    className="flex-1"
                    rules={[{ required: true, message: 'Please enter first name' }]}
                >
                    <Input placeholder="Enter first name" />
                </Form.Item>
                <Form.Item
                    label="Last name"
                    name="lastName"
                    className="flex-1"
                    rules={[{ required: true, message: 'Please enter last name' }]}
                >
                    <Input placeholder="Enter last name" />
                </Form.Item>
            </div>
            <div className={commonS.row}>
                <Form.Item
                    label="Credentials"
                    name="credential"
                    className="flex-1"
                    rules={[{ required: true, message: 'Please select credentials' }]}
                >
                    <Select
                        mode="multiple"
                        fieldNames={{ label: 'content', value: 'dictKey' }}
                        options={credentialTypes as SettingEnum[]}
                    />
                </Form.Item>
                <Form.Item
                    label="Gender"
                    name="gender"
                    className="flex-1"
                    rules={[{ required: true, message: 'Please select gender' }]}
                >
                    <GenderSelect />
                </Form.Item>
            </div>
        </Form>
    );

    const renderReview = () => {
        if (!initValue) return <div>Not provided</div>;
        const { firstName, lastName, credential, gender } = initValue;
        return (
            <div>
                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>First name</div>
                        <div className={commonS.value}>{firstName || '-'}</div>
                    </div>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Last name</div>
                        <div className={commonS.value}>{lastName || '-'}</div>
                    </div>
                </div>
                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Credentials</div>
                        <div className={commonS.value}>{credential || '-'}</div>
                    </div>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Gender</div>
                        <div className={commonS.value}>{gender || '-'}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <FormCard type="review" title="Profile" hasContent onSave={handleSave}>
            {(isEditing) => (isEditing ? renderEditing() : renderReview())}
        </FormCard>
    );
};

export default ProfileCard;
